import React, { useCallback, useMemo, useState } from "react";
import { graphql, useMutation } from "react-relay";

import { Button, Modal } from "@skydio/rivet-ui";

import { displayMutationResults } from "utils/mutations";

import UserSelect from "./UserSelect";

import type { FC } from "react";
import type { Key } from "@skydio/rivet-ui";
import type { AddUserToGroupModalMutation } from "__generated__/AddUserToGroupModalMutation.graphql";

interface AddUserToGroupModalProps {
  groupId: string;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  connection?: string;
}

const AddUserToGroupModal: FC<AddUserToGroupModalProps> = ({
  groupId,
  modalOpen,
  setModalOpen,
  connection,
}) => {
  const [userUuid, setUserUuid] = useState<Key | null>(null);
  const [addUser] = useMutation<AddUserToGroupModalMutation>(graphql`
    mutation AddUserToGroupModalMutation($userUuid: String!, $groupId: ID!, $connections: [ID!]!) {
      setUserGroupEnabled(userUuid: $userUuid, groupId: $groupId, enabled: true) {
        errors
        user @appendNode(connections: $connections, edgeTypeName: "UserEdge") {
          id
          email
          uuid
          organization {
            id
            name
          }
        }
      }
    }
  `);

  const okButtonProps = useMemo(
    () => ({
      disabled: !userUuid,
    }),
    [userUuid]
  );
  const handleClose = useCallback(() => {
    setModalOpen(false);
    setUserUuid(null);
  }, [setModalOpen]);

  const handleOk = useCallback(() => {
    if (userUuid) {
      const uUuid = userUuid as string;
      const innerConnection = connection ? [connection] : [];
      addUser({
        variables: { groupId, userUuid: uUuid, connections: innerConnection },
        onCompleted: displayMutationResults(response => response.setUserGroupEnabled, {
          onSuccess: handleClose,
        }),
      });
    }
  }, [addUser, connection, groupId, handleClose, userUuid]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
      setModalOpen(open);
    },
    [handleClose, setModalOpen]
  );

  return (
    <Modal isOpen={modalOpen} onOpenChange={handleOpenChange}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Add members to group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UserSelect selectedUserUuid={userUuid} setSelectedUserUuid={setUserUuid} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleOk} {...okButtonProps}>
            Add
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export default AddUserToGroupModal;
